import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Returns the URL for displaying an image stored in the DB.
// Handles both legacy file paths (/uploads/...) and base64 data URIs.
// Images are served under /api/uploads (authenticated) so the browser sends
// the session cookie automatically with <img> requests.
export function getStaticUrl(relativeUrl) {
  if (!relativeUrl) return '';
  if (relativeUrl.startsWith('data:')) return relativeUrl;
  const normalized = relativeUrl.startsWith('/uploads/')
    ? relativeUrl.replace('/uploads/', '/api/uploads/')
    : relativeUrl;
  const base = API_BASE.replace(/\/api$/, '');
  return `${base}${normalized}`;
}

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // send HttpOnly cookie on every request
});

// Read a cookie value by name (used for CSRF double-submit).
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

// Handler registered by AuthContext to react to 401s without coupling
// the network layer to React Router or localStorage internals.
let onUnauthorized = null;
export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler;
}

// Attach X-CSRF-Token to all state-mutating requests.
api.interceptors.request.use((config) => {
  const safeMethods = ['get', 'head', 'options'];
  if (!safeMethods.includes((config.method || 'get').toLowerCase())) {
    const csrf = getCookie('concordia_csrf');
    if (csrf) config.headers['X-CSRF-Token'] = csrf;
  }
  return config;
});

// Handle 401 globally — delegate to the registered handler.
// Skip auth endpoints: /auth/login (wrong password), /auth/logout (avoid
// recursion), /auth/me (initial session check — caller handles the error).
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      const url = err.config?.url || '';
      const skip =
        url.endsWith('/auth/login') ||
        url.endsWith('/auth/logout') ||
        url.endsWith('/auth/me');
      if (!skip && onUnauthorized) onUnauthorized();
    }
    return Promise.reject(err);
  }
);

// ─── Auth ───────────────────────────────────────────────────────
export const authAPI = {
  login: (email, password) =>
    api.post('/auth/login', { email, password }),

  logout: () =>
    api.post('/auth/logout'),

  me: () =>
    api.get('/auth/me'),
};

// ─── Contracts ──────────────────────────────────────────────────
export const contractsAPI = {
  list: (params = {}) =>
    api.get('/contracts', { params }),

  get: (id) =>
    api.get(`/contracts/${id}`),

  update: (id, data) =>
    api.patch(`/contracts/${id}`, data),

  getData: (id, params = {}) =>
    api.get(`/contracts/${id}/data`, { params: { limit: 12, ...params } }),

  getDataKeys: (id) =>
    api.get(`/contracts/${id}/data-keys`),

  getIndicators: (id) =>
    api.get(`/contracts/${id}/indicators`),

  addIndicator: (id, data) =>
    api.post(`/contracts/${id}/indicators`, data),

  updateIndicator: (id, indicatorId, data) =>
    api.patch(`/contracts/${id}/indicators/${indicatorId}`, data),

  deleteIndicator: (id, indicatorId) =>
    api.delete(`/contracts/${id}/indicators/${indicatorId}`),

  reorderIndicators: (id, orderedIds) =>
    api.put(`/contracts/${id}/indicators/reorder`, { orderedIds }),
};

// ─── Admin ──────────────────────────────────────────────────────
export const adminAPI = {
  uploadExcel: (file, organizationId, contractId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('organization_id', organizationId);
    if (contractId) formData.append('contract_id', contractId);
    return api.post('/admin/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getUploads: (params = {}) =>
    api.get('/admin/uploads', { params }),

  deleteUpload: (id) =>
    api.delete(`/admin/uploads/${id}`),

  getXmlImports: (params = {}) =>
    api.get('/admin/xml-imports', { params }),

  getUsers: () =>
    api.get('/admin/users'),

  createUser: (data) =>
    api.post('/admin/users', data),

  updateUser: (id, data) =>
    api.patch(`/admin/users/${id}`, data),

  deleteUser: (id) =>
    api.delete(`/admin/users/${id}`),

  runRetention: () =>
    api.post('/admin/run-retention'),
};

// ─── Organizations ──────────────────────────────────────────────
export const orgsAPI = {
  list: () =>
    api.get('/organizations'),

  create: (data) =>
    api.post('/organizations', data),

  update: (orgId, data) =>
    api.put(`/organizations/${orgId}`, data),

  getContracts: (orgId) =>
    api.get(`/organizations/${orgId}/contracts`),

  createContract: (orgId, data) =>
    api.post(`/organizations/${orgId}/contracts`, data),

  deleteContract: (orgId, contractId) =>
    api.delete(`/organizations/${orgId}/contracts/${contractId}`),

  getUsers: (orgId) =>
    api.get(`/organizations/${orgId}/users`),

  removeUser: (orgId, userId) =>
    api.delete(`/organizations/${orgId}/users/${userId}`),
};

// ─── Seguimiento de tareas (plan global y planes semanales) ─────
export const projectsAPI = {
  // scope: 'GLOBAL' (planes del contrato) | 'SEMANAL' (Excel de faena).
  // Para los planes del contrato, planId decide el parser segun su tipo.
  uploadPlan: (contractId, file, scope = 'GLOBAL', planId = null) => {
    const fd = new FormData();
    fd.append('scope', scope);
    if (planId) fd.append('planId', planId);
    fd.append('file', file);
    return api.post(`/projects/${contractId}/upload-plan`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getImports: (contractId, scope = 'GLOBAL') =>
    api.get(`/projects/${contractId}/imports`, { params: { scope } }),

  deleteImport: (contractId, importId) =>
    api.delete(`/projects/${contractId}/imports/${importId}`),

  getTasks: (contractId) =>
    api.get(`/projects/${contractId}/tasks`),

  getSummary: (contractId) =>
    api.get(`/projects/${contractId}/summary`),

  // Semanas cargadas: alimenta el selector y la tendencia de cumplimiento.
  getWeeks: (contractId) =>
    api.get(`/projects/${contractId}/weeks`),

  // Detalle de una semana; sin importId trae la más reciente.
  getWeek: (contractId, importId) =>
    api.get(`/projects/${contractId}/weekly`, { params: importId ? { importId } : {} }),

  // Detalle de un plan del contrato: carga elegida, historial y tareas.
  getPlanDetail: (contractId, planId, importId) =>
    api.get(`/projects/${contractId}/plan/${planId}`, { params: importId ? { importId } : {} }),
};

// ─── Planes del contrato (registro) ─────────────────────────────
export const plansAPI = {
  list: (contractId) =>
    api.get(`/contracts/${contractId}/plans`),

  create: (contractId, data) =>
    api.post(`/contracts/${contractId}/plans`, data),

  update: (contractId, planId, data) =>
    api.patch(`/contracts/${contractId}/plans/${planId}`, data),

  remove: (contractId, planId) =>
    api.delete(`/contracts/${contractId}/plans/${planId}`),
};

// ─── Images ─────────────────────────────────────────────────────
export const imagesAPI = {
  getContractImages: (contractId) =>
    api.get(`/contracts/${contractId}/images`),

  uploadImage: (contractId, file, title = '', description = '') => {
    const fd = new FormData();
    fd.append('image', file);
    fd.append('contract_id', contractId);
    if (title)       fd.append('title', title);
    if (description) fd.append('description', description);
    return api.post('/admin/images', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  updateImage: (imageId, data) =>
    api.patch(`/admin/images/${imageId}`, data),

  deleteImage: (imageId) =>
    api.delete(`/admin/images/${imageId}`),
};

// ─── Improvement Proposals ──────────────────────────────────────
export const proposalsAPI = {
  list:   (contractId) =>
    api.get(`/contracts/${contractId}/proposals`),

  create: (contractId, data) =>
    api.post(`/contracts/${contractId}/proposals`, data),

  update: (contractId, proposalId, data) =>
    api.patch(`/contracts/${contractId}/proposals/${proposalId}`, data),

  remove: (contractId, proposalId) =>
    api.delete(`/contracts/${contractId}/proposals/${proposalId}`),
};

// ─── Contract Documents ─────────────────────────────────────────
export const documentsAPI = {
  list: (contractId) =>
    api.get(`/contracts/${contractId}/documents`),

  create: (contractId, data) =>
    api.post(`/contracts/${contractId}/documents`, data),

  update: (contractId, documentId, data) =>
    api.patch(`/contracts/${contractId}/documents/${documentId}`, data),

  remove: (contractId, documentId) =>
    api.delete(`/contracts/${contractId}/documents/${documentId}`),
};

// ─── Dotación de Personal y Equipos ─────────────────────────────
// ─── Acciones Críticas ──────────────────────────────────────────
export const criticalActionsAPI = {
  // Devuelve { pending, completed }: las dos tablas de la pestaña.
  list: (contractId) =>
    api.get(`/contracts/${contractId}/critical-actions`),

  create: (contractId, data) =>
    api.post(`/contracts/${contractId}/critical-actions`, data),

  // También mueve la acción entre tablas: { completada: true | false }.
  update: (contractId, actionId, data) =>
    api.patch(`/contracts/${contractId}/critical-actions/${actionId}`, data),

  remove: (contractId, actionId) =>
    api.delete(`/contracts/${contractId}/critical-actions/${actionId}`),

  reorder: (contractId, ids) =>
    api.put(`/contracts/${contractId}/critical-actions/reorder`, { ids }),
};

export const staffingAPI = {
  list: (contractId) =>
    api.get(`/contracts/${contractId}/staffing`),

  // Replace-all: `rows` is the full table in display order.
  save: (contractId, rows) =>
    api.put(`/contracts/${contractId}/staffing`, { rows }),
};

// ─── Indicadores SSO ────────────────────────────────────────────
export const ssoAPI = {
  list: (contractId) =>
    api.get(`/contracts/${contractId}/sso`),

  // Replace-all: `rows` is the full table in display order.
  save: (contractId, rows) =>
    api.put(`/contracts/${contractId}/sso`, { rows }),
};

// ─── Reports ────────────────────────────────────────────────────
export const reportsAPI = {
  downloadPDF: async (contractId) => {
    const response = await api.get(`/reports/${contractId}/pdf`, {
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : `Reporte_${contractId}.pdf`;
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

export default api;
